import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Tooltip,
  TablePagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonOff as PersonOffIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
  
const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "0 0 16px 16px",
  background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #004AAD 0%, #3b82f6 100%)",
  },
  "& .MuiTableCell-head": {
    color: "white",
    fontWeight: 600,
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    borderBottom: "none",
    padding: "14px 16px",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0,74,173,0.04)",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "rgba(0,74,173,0.02)",
    },
  },
  "& .MuiTableCell-body": {
    padding: "12px 16px",
    borderBottom: "1px solid rgba(0,74,173,0.06)",
    fontSize: "0.9rem",
  },
}));

const StyledAvatar = styled(Avatar)(() => ({
  width: 44,
  height: 44,
  border: "2px solid #004AAD",
  boxShadow: "0 2px 8px rgba(0,74,173,0.15)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 4px 12px rgba(0,74,173,0.25)",
  },
}));

const StatusChip = styled(Chip)(() => ({
  backgroundColor: "#fee2e2",
  color: "#dc2626",
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 26,
  "& .MuiChip-icon": { color: "#dc2626" },
}));

const SearchField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "white",
    transition: "all 0.3s ease",
    "&:hover": { boxShadow: "0 2px 12px rgba(0,74,173,0.12)" },
    "&.Mui-focused": {
      boxShadow: "0 4px 16px rgba(0,74,173,0.15)",
      "& fieldset": { borderColor: "#004AAD", borderWidth: 2 },
    },
  },
}));

const SectionHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: "16px 20px",
  background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
  borderRadius: "16px 16px 0 0",
  borderBottom: "2px solid rgba(0,74,173,0.08)",
  flexWrap: "wrap",
  gap: "12px",
}));

const ExpandableRow = styled(TableRow)(() => ({
  backgroundColor: "rgba(0,74,173,0.02)",
  "& > td": { paddingBottom: 0 },
}));
 

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : parts[0][0].toUpperCase();
};

 
const avatarColors = [
  { bg: "#e6f1fb", color: "#0c447c" },
  { bg: "#e1f5ee", color: "#085041" },
  { bg: "#eeedfe", color: "#3c3489" },
  { bg: "#faeeda", color: "#633806" },
  { bg: "#faece7", color: "#712b13" },
];
const getAvatarColor = (name = "") => {
  const idx = name.charCodeAt(0) % avatarColors.length;
  return avatarColors[idx];
};

 

const MobileEmployeeCard = ({ employee, imageErrors, onImageError, getImageUrl }) => {
  const [open, setOpen] = useState(false);
  const imageError = imageErrors[employee.sno];
  const { bg, color } = getAvatarColor(employee.repname);

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: "14px",
        border: open
          ? "1.5px solid rgba(0,74,173,0.25)"
          : "1px solid rgba(0,74,173,0.1)",
        boxShadow: open
          ? "0 4px 16px rgba(0,74,173,0.12)"
          : "0 2px 6px rgba(0,74,173,0.05)",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      }}
    >
      {/* ── Main tap row ── */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: "14px",
          cursor: "pointer",
          background: "white",
          userSelect: "none",
          "&:active": { backgroundColor: "rgba(0,74,173,0.03)" },
        }}
      >
        {/* Avatar */}
        <Avatar
          src={!imageError ? getImageUrl(employee.sno) : ""}
          onError={() => onImageError(employee.sno)}
          sx={{
            width: 48,
            height: 48,
            border: `2px solid ${imageError ? "rgba(0,74,173,0.15)" : "#004AAD"}`,
            bgcolor: imageError ? bg : "transparent",
            color,
            fontWeight: 600,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          {imageError && getInitials(employee.repname)}
        </Avatar>

        {/* Name + badges */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.92rem",
              color: "#1e293b",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {employee.repname || "N/A"}
          </Typography>

          {/* Info badges row */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", mt: "6px" }}>
            {/* Service No */}
            <Chip
              label={employee.sno || "—"}
              size="small"
              icon={<BadgeIcon sx={{ fontSize: "12px !important" }} />}
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontFamily: "monospace",
                fontWeight: 600,
                backgroundColor: "#e6f1fb",
                color: "#0c447c",
                "& .MuiChip-icon": { color: "#185fa5", ml: "6px" },
                "& .MuiChip-label": { px: "6px" },
              }}
            />
            {/* Designation */}
            {employee.des && (
              <Chip
                label={employee.des}
                size="small"
                icon={<WorkIcon sx={{ fontSize: "12px !important" }} />}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  "& .MuiChip-icon": { color: "#94a3b8", ml: "6px" },
                  "& .MuiChip-label": { px: "6px" },
                }}
              />
            )}
            {/* Location */}
            {employee.loc && (
              <Chip
                label={employee.loc}
                size="small"
                icon={<LocationIcon sx={{ fontSize: "12px !important" }} />}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  "& .MuiChip-icon": { color: "#94a3b8", ml: "6px" },
                  "& .MuiChip-label": { px: "6px" },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Right: Absent badge + expand */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <StatusChip
            label="Absent"
            size="small"
            icon={<PersonOffIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: open ? "#004AAD" : "rgba(0,74,173,0.2)",
              background: open ? "rgba(0,74,173,0.06)" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
          >
            {open ? (
              <ExpandLessIcon sx={{ fontSize: 18, color: "#004AAD" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Expanded detail panel ── */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            borderTop: "1px solid rgba(0,74,173,0.1)",
            background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
            p: "14px 14px 16px",
          }}
        >
          {/* Photo */}
          {!imageError && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Avatar
                src={getImageUrl(employee.sno)}
                onError={() => onImageError(employee.sno)}
                sx={{
                  width: 80,
                  height: 80,
                  border: "3px solid #004AAD",
                  boxShadow: "0 4px 16px rgba(0,74,173,0.18)",
                }}
              />
            </Box>
          )}

          {/* Detail grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 16px",
            }}
          >
            {[
              { label: "Full name", value: employee.repname },
              { label: "Service no.", value: employee.sno, mono: true },
              { label: "Designation", value: employee.des },
              { label: "Location", value: employee.loc },
            ].map(({ label, value, mono, danger }) => (
              <Box key={label}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#94a3b8",
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: "3px",
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    color: danger ? "#dc2626" : "#1e293b",
                    fontFamily: mono ? "monospace" : "inherit",
                    lineHeight: 1.4,
                  }}
                >
                  {value || "N/A"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AbsentEmployeesList = ({ data, loading, onRefresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (sno) =>
    setImageErrors((prev) => ({ ...prev, [sno]: true }));

  const getImageUrl = (sno) =>
    `https://esystems.cdl.lk/backendDGES/BizTrack/Home/DGESGetUserImg?serviceNo=${sno}`;

  const filteredData =
    data?.filter((employee) => {
      const q = searchTerm.toLowerCase();
      return (
        employee.loc?.toLowerCase().includes(q) ||
        employee.sno?.toLowerCase().includes(q) ||
        employee.des?.toLowerCase().includes(q) ||
        employee.repname?.toLowerCase().includes(q)
      );
    }) || [];

  const paginatedData = filteredData;

  const totalCount = filteredData.length;

  const handleRowClick = (sno) =>
    setExpandedRow(expandedRow === sno ? null : sno);
 
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
          background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
          borderRadius: "16px",
        }}
      >
        <CircularProgress sx={{ color: "#004AAD" }} />
      </Box>
    );
  }

 
  if (!data || data.length === 0) {
    return (
      <Paper
        sx={{
          borderRadius: "16px",
          padding: "40px",
          textAlign: "center",
          background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
        }}
      >
        <PersonOffIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 2 }} />
        <Typography variant="h6" sx={{ color: "#475569", fontWeight: 600 }}>
          No Absent Employees
        </Typography>
        <Typography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
          All employees are present today.
        </Typography>
      </Paper>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Paper
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
          boxShadow: "0 4px 24px rgba(0,74,173,0.08)",
        }}
      >
        {/* ─── Header ───────────────────────────────────────────────── */}
        <SectionHeader>
          {/* Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: "#fee2e2",
                borderRadius: "12px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <PersonOffIcon sx={{ color: "#dc2626" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Absent Employees
                <Chip
                  label={totalCount}
                  size="small"
                  sx={{
                    ml: 1.5,
                    backgroundColor: "#dc2626",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    height: 24,
                    minWidth: 32,
                  }}
                />
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Today's absentees list
              </Typography>
            </Box>
          </Box>

          {/* Search */}
          <SearchField
            size="small"
            placeholder="Search by name, location..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 260 } }}
          />
        </SectionHeader>

        {/* ─── Mobile Card List ──────────────────────────────────────── */}
        {isMobile && (
          <Box
            sx={{
              p: "12px 12px 0",
              maxHeight: "65vh",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,74,173,0.2)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(0,74,173,0.4)",
              },
            }}
          >
            {paginatedData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <PersonOffIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                  No results found
                </Typography>
              </Box>
            ) : (
              paginatedData.map((employee) => (
                <MobileEmployeeCard
                  key={employee.sno}
                  employee={employee}
                  imageErrors={imageErrors}
                  onImageError={handleImageError}
                  getImageUrl={getImageUrl}
                />
              ))
            )}
          </Box>
        )}

        {/* ─── Desktop Table ─────────────────────────────────────────── */}
        {!isMobile && (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Service No.</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell sx={{ width: 100 }}>Status</TableCell>
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((employee) => {
                  const isExpanded = expandedRow === employee.sno;
                  const imageError = imageErrors[employee.sno];

                  return (
                    <React.Fragment key={employee.sno}>
                      <TableRow
                        hover
                        onClick={() => handleRowClick(employee.sno)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0,74,173,0.04)!important",
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <StyledAvatar
                              src={!imageError ? getImageUrl(employee.sno) : ""}
                              onError={() => handleImageError(employee.sno)}
                              sx={{ bgcolor: imageError ? "#e2e8f0" : "transparent" }}
                            >
                              {imageError && getInitials(employee.repname)}
                            </StyledAvatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                                {employee.repname || "N/A"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                {employee.cno || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Tooltip title="Service Number">
                            <Chip
                              label={employee.sno}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(0,74,173,0.08)",
                                color: "#004AAD",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <WorkIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                            <Typography sx={{ color: "#475569", fontSize: "0.85rem" }}>
                              {employee.des || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                            <Typography sx={{ color: "#475569", fontSize: "0.85rem" }}>
                              {employee.loc || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <StatusChip
                            label="Absent"
                            icon={<PersonOffIcon sx={{ fontSize: 16 }} />}
                          />
                        </TableCell>

                        <TableCell>
                          <IconButton size="small">
                            {isExpanded ? (
                              <ExpandLessIcon sx={{ color: "#004AAD" }} />
                            ) : (
                              <ExpandMoreIcon sx={{ color: "#94a3b8" }} />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expandable detail row */}
                      <ExpandableRow>
                        <TableCell colSpan={6} sx={{ py: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 3, px: 4 }}>
                              <Card
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                                  borderRadius: "12px",
                                }}
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        sm: "1fr 1fr",
                                        md: "1fr 1fr 1fr",
                                      },
                                      gap: 3,
                                    }}
                                  >
                                    {[
                                      { label: "Full Name", value: employee.repname },
                                      {
                                        label: "Service Number",
                                        value: employee.sno,
                                        mono: true,
                                      },
                                      { label: "Contact Number", value: employee.cno },
                                      { label: "Designation", value: employee.des },
                                      { label: "Location", value: employee.loc },
                                    ].map(({ label, value, mono }) => (
                                      <Box key={label}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: "#94a3b8" }}
                                        >
                                          {label}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontWeight: 600,
                                            color: "#1e293b",
                                            fontFamily: mono ? "monospace" : "inherit",
                                          }}
                                        >
                                          {value || "N/A"}
                                        </Typography>
                                      </Box>
                                    ))}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#94a3b8" }}
                                      >
                                        Status
                                      </Typography>
                                      <Box sx={{ mt: 0.5 }}>
                                        <StatusChip label="Absent" size="small" />
                                      </Box>
                                    </Box>
                                  </Box>

                                  {!imageError && (
                                    <Box
                                      sx={{
                                        mt: 3,
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Avatar
                                        src={getImageUrl(employee.sno)}
                                        onError={() => handleImageError(employee.sno)}
                                        sx={{
                                          width: 120,
                                          height: 120,
                                          border: "4px solid #004AAD",
                                          boxShadow: "0 8px 32px rgba(0,74,173,0.2)",
                                        }}
                                      />
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </ExpandableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}


      </Paper>
    </Fade>
  );
};

export default AbsentEmployeesList;